import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PackageCard from './PackageCard';
import { GET_MARKETPLACE_PACKAGES } from '../graphql/queries/package';
import type { PackageSearchFilters } from '../types/marketplaceSearch';
import {
  EMPTY_PACKAGE_SEARCH_FILTERS,
  paramsFromPackageSearchFilters,
} from '../types/marketplaceSearch';
import { filterPackagesBySearch } from '../utils/marketplacePackageFilter';

type MarketplacePackagesQuery = {
  packages: Array<{
    id: number;
    title: string;
    description: string;
    image: string;
    groupSize: string;
    category: string;
    type: 'B2B' | 'B2C';
    departure: string;
    cities: string[];
    countries: string[];
    departures: Array<{
      id: number;
      departureDate: string;
      returnDate: string;
    }>;
    formulas: Array<{
      id: number;
      name: string;
      priceType: string;
      perPersonPrice: number | null;
      roomPrices: {
        single: number;
        double: number;
        triple: number;
        quadruple: number;
        quintuple: number;
      };
    }>;
    agency: {
      id: number;
      name: string;
      logo: string | null;
      rating: number | null;
      verified: boolean;
      theme: { primaryColor: string | null; secondaryColor: string | null } | null;
    };
    airline?: {
      id: number;
      name: string;
      code: string | null;
      logo: string;
    } | null;
  }>;
};

/** Nombre maximum de séjours affichés sur la page d’accueil (aperçu). */
const HOME_OFFERS_LIMIT = 6;

/** Couleur d’accent carte (bleu site) — le fond du logo agence reste le thème agence. */
const MARKETPLACE_HOME_PRIMARY = '#2563eb';

/**
 * Grille de packages pour l’accueil marketplace : même rendu que la section « Nos séjours »
 * des sites agence (PackageCard), sans dépendre du bundle frontend-agency.
 */
type Props = {
  searchFilters?: PackageSearchFilters;
};

export default function MarketplaceHomePackages({
  searchFilters = EMPTY_PACKAGE_SEARCH_FILTERS,
}: Props) {
  const { data, loading, error } = useQuery<MarketplacePackagesQuery>(GET_MARKETPLACE_PACKAGES);

  if (loading) {
    return (
      <section id="packages" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="packages" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 text-center text-red-600 py-12">
          Impossible de charger les séjours pour le moment.
        </div>
      </section>
    );
  }

  const b2cAll = (data?.packages ?? []).filter((p) => p.type === 'B2C');
  const b2c = filterPackagesBySearch(b2cAll, searchFilters);
  const b2cHomePreview = b2c.slice(0, HOME_OFFERS_LIMIT);

  const isFiltered =
    searchFilters.destination.trim() !== '' ||
    searchFilters.category !== '' ||
    searchFilters.month !== '' ||
    searchFilters.duration !== '' ||
    searchFilters.packageType !== '' ||
    searchFilters.airlineId !== '';

  const offresHref = (() => {
    if (!isFiltered) return '/offres';
    const q = paramsFromPackageSearchFilters(searchFilters).toString();
    return q ? `/offres?${q}` : '/offres';
  })();

  return (
    <section id="packages" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">Nos séjours</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Des séjours proposés par nos agences partenaires — comparez les dates et les tarifs
            comme sur une page agence Ziyara.
          </p>
          {isFiltered && b2c.length > 0 && (
            <p className="text-sm text-gray-500 mt-3">
              {b2c.length} résultat{b2c.length !== 1 ? 's' : ''}
              {b2c.length < b2cAll.length ? ` sur ${b2cAll.length} séjour${b2cAll.length !== 1 ? 's' : ''}` : ''}
              {b2c.length > HOME_OFFERS_LIMIT
                ? ` — aperçu des ${HOME_OFFERS_LIMIT} premières sur cette page`
                : ''}
            </p>
          )}
        </div>

        {b2cAll.length === 0 ? (
          <p className="text-center text-gray-500 py-12">
            Aucun séjour grand public pour le moment. Revenez bientôt ou parcourez nos agences.
          </p>
        ) : b2c.length === 0 ? (
          <p className="text-center text-gray-500 py-12">
            Aucun séjour ne correspond à votre recherche. Modifiez les critères dans la barre de
            recherche en haut de page.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {b2cHomePreview.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  package={{
                    ...pkg,
                    departures: pkg.departures ?? [],
                    airline: pkg.airline ?? undefined,
                    agency: pkg.agency
                      ? {
                          id: pkg.agency.id,
                          name: pkg.agency.name,
                          logo: pkg.agency.logo ?? '',
                          rating: pkg.agency.rating ?? undefined,
                          verified: pkg.agency.verified,
                          secondaryColor: pkg.agency.theme?.secondaryColor ?? null,
                        }
                      : undefined,
                  }}
                  primaryColor={MARKETPLACE_HOME_PRIMARY}
                />
              ))}
            </div>
            <div className="mt-10 flex justify-center">
              <Link
                to={offresHref}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Voir tous les séjours
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
